import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import qrcode from 'qrcode-generator';
import { Check, Copy, Unplug } from 'lucide-react';
import { cn } from '../lib/cn';
import { server } from './config';
import {
  checkServer,
  connect,
  currentConnection,
  disconnect,
  normalizeUrl,
  parseInvite,
  type CheckResult,
  type Connection,
} from './connect';
import { useSyncState } from './useSync';

const field = 'w-full rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';
const primary =
  'rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg disabled:opacity-50';
const secondary =
  'flex items-center gap-1.5 rounded-lg border border-border px-2 py-1 text-sm hover:bg-bg-hover disabled:opacity-50';

function useCheckMessage() {
  const { t } = useTranslation();
  return (r: Exclude<CheckResult, { ok: true }>) => t(`server.check.${r.reason}`);
}

/** Check the server, then keep the connection and restart with it. */
async function tryConnect(
  conn: Connection,
  message: (r: Exclude<CheckResult, { ok: true }>) => string,
): Promise<string | null> {
  const result = await checkServer(conn);
  if (!result.ok) return message(result);
  connect(conn);
  return null;
}

/** No server yet: connect with an invite, or with the project's URL and key. */
export function ConnectForm() {
  const { t } = useTranslation();
  const message = useCheckMessage();
  const [invite, setInvite] = useState('');
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    let conn: Connection | null;
    if (invite.trim()) {
      conn = parseInvite(invite);
      if (!conn) return setError(t('server.badInvite'));
    } else {
      try {
        conn = { url: normalizeUrl(url), key: key.trim() };
      } catch (e) {
        return setError(
          t(e instanceof Error && e.message === 'insecure' ? 'server.insecure' : 'server.badUrl'),
        );
      }
    }
    setBusy(true);
    try {
      setError(await tryConnect(conn, message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      aria-label={t('server.connectTitle')}
      className="flex flex-col gap-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p>{t('sync.noServer')}</p>
      <p className="text-xs text-fg-muted">{t('server.intro')}</p>
      <label className="flex flex-col gap-1">
        {t('server.invite')}
        <input
          value={invite}
          onChange={(e) => setInvite(e.target.value)}
          placeholder="nexus-open:…"
          className={field}
          spellCheck={false}
        />
      </label>
      <details className="rounded-lg border border-border p-2" open={!!url || !!key}>
        <summary className="cursor-pointer text-xs text-fg-muted">{t('server.manual')}</summary>
        <div className="mt-2 flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            {t('server.url')}
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://xyz.supabase.co"
              className={field}
              spellCheck={false}
            />
          </label>
          <label className="flex flex-col gap-1">
            {t('server.key')}
            <input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="sb_publishable_…"
              className={field}
              spellCheck={false}
            />
          </label>
          <p className="text-xs text-fg-muted">{t('server.keyHint')}</p>
        </div>
      </details>
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || (!invite.trim() && (!url.trim() || !key.trim()))}
        className={cn(primary, 'self-start')}
      >
        {busy ? t('server.checking') : t('server.connect')}
      </button>
      <SetupHelp />
    </form>
  );
}

const SETUP_COMMAND =
  'SUPABASE_ACCESS_TOKEN=… pnpm setup:supabase --project-ref <ref> --owner <jouw e-mailadres>';

/** How to set up your own server, step by step (the same as the README's "Eigen server"). */
function SetupHelp() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const steps = t('server.setup.steps', { returnObjects: true }) as string[];
  return (
    <details className="rounded-lg border border-border p-2">
      <summary className="cursor-pointer text-xs text-fg-muted">{t('server.setup.title')}</summary>
      <div className="mt-2 flex flex-col gap-2 text-xs">
        <p className="text-fg-muted">{t('server.setup.intro')}</p>
        <ol className="flex list-decimal flex-col gap-1.5 pl-5">
          {steps.map((step, i) => (
            <li key={i}>
              {step}
              {i === 1 && (
                <div className="mt-1 flex items-start gap-1">
                  <code className="min-w-0 flex-1 rounded-md bg-bg-subtle px-2 py-1 break-all">
                    {SETUP_COMMAND}
                  </code>
                  <button
                    type="button"
                    className={secondary}
                    aria-label={t('server.setup.copy')}
                    title={t('server.setup.copy')}
                    onClick={() =>
                      void navigator.clipboard.writeText(SETUP_COMMAND).then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      })
                    }
                  >
                    {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ol>
        <p className="text-fg-muted">{t('server.setup.after')}</p>
      </div>
    </details>
  );
}

/** The server this device uses, and disconnecting. The invite link/QR for
 * colleagues lives in Beheer (ManagementSettings' ServerInvite) -- who's
 * allowed onto the project is the owner/beheerder's call, not something
 * every user needs to see here in Account. */
export function ServerInfo() {
  const { t } = useTranslation();
  const session = useSyncState((s) => s.session);
  const conn = currentConnection();
  const cfg = server();
  if (!conn) return null;

  return (
    <div className="mb-4 flex flex-col gap-2 border-b border-border pb-4 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-fg-muted">{t('server.connectedTo')}</span>
        <code className="truncate text-xs">{new URL(conn.url).host}</code>
      </div>
      {cfg.source === 'device' && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={secondary}
            disabled={!!session}
            title={session ? t('server.signOutFirst') : undefined}
            onClick={disconnect}
          >
            <Unplug className="size-3.5" />
            {t('server.disconnect')}
          </button>
        </div>
      )}
    </div>
  );
}

export function Qr({ text, label }: { text: string; label: string }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [text]);
  return (
    <div
      role="img"
      aria-label={label}
      className="size-48 self-start rounded-lg bg-white p-1"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/** Opened from an invite link: connect this device to that server? */
export function ConnectPrompt({ conn, onSkip }: { conn: Connection; onSkip: () => void }) {
  const { t } = useTranslation();
  const message = useCheckMessage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = currentConnection();
  return (
    <main className="grid min-h-full place-items-center p-6">
      <div
        role="dialog"
        aria-labelledby="connect-title"
        className="flex w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-bg-subtle p-6 shadow-sm"
      >
        <div className="flex items-center gap-2">
          <img src="/icon.svg" alt="" className="size-7" />
          <h1 id="connect-title" className="text-lg font-semibold">
            {t('server.promptTitle')}
          </h1>
        </div>
        <p className="text-sm">{t('server.promptBody', { host: new URL(conn.url).host })}</p>
        {current && current.url !== conn.url && (
          <p className="text-xs text-danger">
            {t('server.promptReplace', { host: new URL(current.url).host })}
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm hover:bg-bg-hover"
            onClick={onSkip}
          >
            {t('app.cancel')}
          </button>
          <button
            type="button"
            disabled={busy}
            className={primary}
            onClick={() => {
              setBusy(true);
              void tryConnect(conn, message)
                .then(setError)
                .finally(() => setBusy(false));
            }}
          >
            {busy ? t('server.checking') : t('server.connect')}
          </button>
        </div>
      </div>
    </main>
  );
}
